import React, { createContext, useContext, useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getInitials } from '../../utils/avatar';
import { LogOut, User, Users, Facebook, Bot } from 'lucide-react';

const ThreadsIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 192 192" fill="currentColor" aria-hidden="true">
    <path d="M141.537 88.988c-2.406-1.125-4.876-2.107-7.404-2.94-.78-3.169-1.8-6.076-3.034-8.668
      C125.19 62.595 113.617 55.244 99.967 55.244c-9.547 0-18.386 3.463-25.064 9.914
      C68.8 71.01 65.15 79.148 64.374 88.617a54.27 54.27 0 0 0-7.386 2.97l1.944 3.4
      a50.46 50.46 0 0 1 5.876-2.347c-.266 3.44-.186 7.12.298 10.987
      a46.06 46.06 0 0 0 2.352 9.196c6.726 18.278 23.41 28.98 43.77 28.98
      c7.822 0 15.006-1.82 21.345-5.374a37.22 37.22 0 0 0 8.673-7.0
      c5.047-5.69 8.118-13.104 8.273-21.397a64.33 64.33 0 0 1 5.666 2.008l2.155-3.762
      c-7.46-3.015-15.614-4.798-23.993-4.29zm-41.57 31.23
      c-16.624 0-29.546-9.223-35.18-24.443a39.96 39.96 0 0 1-1.927-7.317
      c-.628-4.347-.458-8.535.268-12.398
      c1.16-6.368 3.892-11.672 7.926-15.433
      C76.096 56.15 87.035 51.46 105.967 51.46
      c17.7 0 30.215 8.147 36.166 22.785.842 2.043 1.576 4.163 2.196 6.346
      a80.58 80.58 0 0 0-11.34-.509c-5.875 0-11.388.534-16.422 1.569v4.106
      c4.873-1.018 10.212-1.548 15.904-1.548 4.444 0 8.75.362 12.889 1.042
      .263 4.262-.208 8.326-1.359 12.026-1.157 3.803-3.083 7.172-5.68 9.967
      a30.47 30.47 0 0 1-6.975 5.685c-5.247 3.022-11.487 4.618-18.544 4.618z"/>
  </svg>
);
import { useModal } from '../ui/ModalProvider';
import ProfileSwitcher from './ProfileSwitcher';

// ─── Custom brand icon components ───────────────────────────────────────────

const WordPressIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" fill="#21759B"/>
    <polyline
      points="5.5,9 8,17 10.5,10.5 12,15.5 13.5,10.5 16,17 18.5,9"
      fill="none"
      stroke="white"
      strokeWidth="2.2"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
);

const PinterestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="#E60023">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const ScissorsIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6" cy="6" r="3"/>
    <circle cx="6" cy="18" r="3"/>
    <line x1="20" y1="4" x2="8.12" y2="15.88"/>
    <line x1="14.47" y1="14.48" x2="20" y2="20"/>
    <line x1="8.12" y1="8.12" x2="12" y2="12"/>
  </svg>
);

// White "E" lettermark — fill uses currentColor so it adapts to parent color
const EtsyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <rect x="6.5" y="3.5" width="11" height="2.5" rx="0.4"/>
    <rect x="6.5" y="3.5" width="2.5" height="17" rx="0.4"/>
    <rect x="6.5" y="10.5" width="7.5" height="2.5" rx="0.4"/>
    <rect x="6.5" y="18" width="11" height="2.5" rx="0.4"/>
  </svg>
);

// Home icon — same path as the Dashboard nav item
const HomeIcon = ({ size = 16, color = '#166534' }) => (
  <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);
  const toggleMobile = () => setIsMobileOpen(!isMobileOpen);
  const closeMobile = () => setIsMobileOpen(false);

  return (
    <SidebarContext.Provider value={{ isCollapsed, isMobileOpen, toggleCollapse, toggleMobile, closeMobile }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) throw new Error('useSidebar must be used within SidebarProvider');
  return context;
};

export default function Sidebar() {
  const navigate = useNavigate();
  const { user, userProfile, signOut, isManager, profileLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const { isMobileOpen, toggleMobile, closeMobile } = useSidebar();
  const location = useLocation();
  const { alert, confirm } = useModal();

  // selfColored=true → icon handles its own fill/stroke, skip style.color
  const categories = [
    {
      id: 'wordpress',
      title: 'WORDPRESS',
      icon: WordPressIcon,
      bgColor: '#E8F5E9',
      accentColor: '#21759B',
      selfColored: true,
      items: [
        { label: 'Blog Posts', path: '/wp/posts' },
        { label: 'Add Blog Post', path: '/wp/add-post' },
        { label: 'Sites', path: '/wp/sites' },
        { label: 'Copy Blogs', path: '/wp/copy-blogs' },
      ],
    },
    {
      id: 'pinterest',
      title: 'PINTEREST',
      icon: PinterestIcon,
      bgColor: '#FDE9EC',
      accentColor: '#E60023',
      selfColored: true,
      items: [
        { label: 'Accounts', path: '/pin/accounts' },
        { label: 'Create Pin', path: '/pin/create' },
        { label: 'Pinterest Audit', path: '/pin/trend-keywords' },
        { label: 'Performance Analytics', path: '/pin/performance-analytics' },
        { label: 'Recovery Center', path: '/pin/recovery' },
        { label: 'Pin Library', path: '/pin/library' },
      ],
    },
    {
      id: 'facebook',
      title: 'FACEBOOK',
      icon: Facebook,
      bgColor: '#EAF3FF',
      accentColor: '#1877F2',
      items: [
        { label: 'Page Stats', path: '/fb/page-stats' },
        { label: 'Posts Planner', path: '/fb/posts-planner' },
      ],
    },
    {
      id: 'threads',
      title: 'THREADS',
      icon: ThreadsIcon,
      bgColor: '#F3F4F6',
      accentColor: '#374151',
      headerTextColor: '#374151',
      selfColored: false,
      items: [
        { label: 'Thread Accounts', path: '/threads/accounts' },
        { label: 'Instagram Accounts', path: '/threads/instagram-accounts' },
        { label: 'Create Thread / Instagram', path: '/threads/create' },
      ],
    },
    {
      id: 'etsy',
      title: 'ETSY ACCOUNT',
      icon: EtsyIcon,
      bgColor: '#FFF3E0',
      accentColor: '#E55300',
      headerTextColor: '#E55300',
      selfColored: false,
      items: [
        { label: 'Accounts', path: '/etsy/accounts' },
        { label: 'Digital Products', path: '/etsy/digital-products' },
      ],
    },
    {
      id: 'pattern-studio',
      title: 'PATTERN STUDIO',
      icon: ScissorsIcon,
      bgColor: '#FAF7F4',
      accentColor: '#6B5744',
      headerTextColor: '#6B5744',
      selfColored: false,
      items: [
        { label: 'My Patterns', path: '/etsy/pattern-studio' },
        { label: 'New Pattern', path: '/etsy/pattern-studio/new' },
      ],
    },
    {
      id: 'tools',
      title: 'TOOLS',
      icon: Bot,
      bgColor: '#F3E8FF',
      accentColor: '#6D28D9',
      items: [
        { label: 'Prompts', path: '/tools/prompts' },
        { label: 'Add Prompt', path: '/tools/add-prompt' },
        { label: 'Settings', path: '/tools/settings' },
      ],
    },
  ];

  const handleSignOut = async () => {
    if (await confirm('Are you sure you want to sign out?')) {
      setLoading(true);
      const { error } = await signOut();
      if (!error) {
        navigate('/signin');
      } else {
        alert('Failed to sign out. Please try again.');
        setLoading(false);
      }
    }
  };

  const handleProfileClick = () => navigate('/profile');

  // Soft green circle style shared by header icon circles
  const greenCircle = {
    backgroundColor: '#D4EDDA',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    minWidth: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={toggleMobile}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        aria-label="Toggle mobile menu"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isMobileOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed, always expanded */}
      <aside
        className={`fixed top-0 left-0 h-screen bg-white z-40 flex flex-col transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        style={{
          width: '260px',
          boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
          fontFamily: "'Inter','Poppins',sans-serif",
        }}
      >
        {/* ── Logo header: [Logo] Creative Brains [Home Circle] ── */}
        <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center" style={{ gap: '8px' }}>
            {/* Left: Creative Brains logo */}
            <img
              src="https://gymplix.com/wp-content/uploads/2026/02/Creative-brains-logoV01.webp"
              alt="Creative Brains"
              style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
            {/* Center: Brand name — flex-1 + text-center ensures perfect centering */}
            <span
              className="text-lg font-semibold text-gray-900"
              style={{ flex: 1, textAlign: 'center', whiteSpace: 'nowrap' }}
            >
              Creative Brains
            </span>
            {/* Right: Home / Dashboard circle — same size as logo */}
            <NavLink
              to="/automation-control-panel"
              style={{ ...greenCircle, textDecoration: 'none' }}
              aria-label="Go to Dashboard"
              title="Dashboard"
            >
              <HomeIcon size={16} color="#166534" />
            </NavLink>
          </div>
        </div>

        {/* ── User profile: [User Circle] Info [Users Circle] ── */}
        {user && (
          <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center rounded-lg" style={{ gap: '8px', padding: '6px' }}>
              {/* Left: User avatar circle — managers go to All Users, others do nothing */}
              <div
                onClick={(!profileLoading && isManager) ? () => navigate('/admin/users') : undefined}
                className={`bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center overflow-hidden transition-opacity ${(!profileLoading && isManager) ? 'cursor-pointer hover:opacity-90' : 'cursor-default'}`}
                style={{ width: '32px', height: '32px', minWidth: '32px', flexShrink: 0 }}
              >
                {userProfile?.avatar_url ? (
                  <img src={userProfile.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : userProfile?.full_name ? (
                  <span className="text-white text-xs font-semibold">{getInitials(userProfile.full_name, user?.email)}</span>
                ) : (
                  <User className="h-4 w-4 text-white" />
                )}
              </div>

              {/* Center: email only — click opens personal profile */}
              <div
                onClick={handleProfileClick}
                className="cursor-pointer min-w-0"
                style={{ flex: 1, textAlign: 'center' }}
              >
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>

              {/* Right: Users icon circle (manager) or symmetry spacer */}
              {(!profileLoading && isManager) ? (
                <NavLink
                  to="/admin/users"
                  style={{ ...greenCircle, textDecoration: 'none' }}
                  aria-label="Manage Users"
                  title="Users"
                >
                  <Users className="h-4 w-4" style={{ color: '#166534' }} />
                </NavLink>
              ) : (
                <div style={{ width: '32px', height: '32px', minWidth: '32px', flexShrink: 0 }} />
              )}
            </div>
          </div>
        )}

        {/* Active language profile — scopes WordPress/Pinterest/Facebook/Threads
            accounts and content below. Etsy is unaffected by this. */}
        {user && <ProfileSwitcher />}

        {/* Navigation — always fully expanded, no toggles, no arrows */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {categories.map((category) => {
            const CategoryIcon = category.icon;
            const isActive = category.items.some(item => location.pathname === item.path);
            const headerText = category.headerTextColor || category.accentColor;

            return (
              <div key={category.id} className="mb-1.5">
                {/* Section label — static div, no click handler */}
                <div
                  className="flex items-center px-3 py-2 rounded-lg"
                  style={{
                    backgroundColor: category.bgColor,
                    borderLeft: isActive ? `3px solid ${headerText}` : 'none',
                    paddingLeft: isActive ? '9px' : '12px',
                  }}
                >
                  {category.selfColored ? (
                    <CategoryIcon size={16} />
                  ) : (
                    <CategoryIcon size={16} style={{ color: headerText }} />
                  )}
                  <span
                    className="ml-2 text-xs font-semibold tracking-wide"
                    style={{ color: headerText }}
                  >
                    {category.title}
                  </span>
                </div>

                {/* Sub-items — always visible */}
                <div className="mt-0.5 space-y-0.5">
                  {category.items.map((item) => {
                    const isItemActive = location.pathname === item.path;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={closeMobile}
                        className="flex items-center py-1.5 rounded-lg transition-all duration-200 ml-3 bg-white hover:bg-gray-50"
                        style={{
                          borderLeft: isItemActive ? `3px solid ${category.accentColor}` : 'none',
                          paddingLeft: isItemActive ? '10px' : '13px',
                        }}
                      >
                        <span
                          className="text-sm"
                          style={{
                            color: isItemActive ? category.accentColor : '#4B5563',
                            fontWeight: isItemActive ? '500' : '400',
                          }}
                        >
                          {item.label}
                        </span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Sign out */}
        <div className="px-3 py-3 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={handleSignOut}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogOut className="h-4 w-4" />
            <span className="font-medium text-sm">{loading ? 'Signing out...' : 'Sign Out'}</span>
          </button>
        </div>
      </aside>

      {/* Main content spacer */}
      <div className="hidden lg:block" style={{ width: '260px' }} />
    </>
  );
}
